import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Activity,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  Eye,
  X,
  Loader2,
  TrendingUp,
  Package,
  Upload,
  UserCheck
} from 'lucide-react';
import { Job, BatchStatus, jobService } from '../../services/job';

interface JobMonitorProps {
  batchId: string;
  onClose: () => void;
  onViewJobs?: () => void;
  onViewApproval?: () => void;
  onBulkReject?: (jobIds: string[], reason?: string) => void;
}

export function JobMonitor({ batchId, onClose, onViewJobs, onViewApproval, onBulkReject }: JobMonitorProps) {
  const [batchStatus, setBatchStatus] = useState<BatchStatus | null>(null);
  const [recentJobs, setRecentJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    loadBatchStatus();
    loadRecentJobs();

    // Set up auto-refresh for active batches
    const interval = setInterval(() => {
      if (autoRefresh) {
        loadBatchStatus();
        loadRecentJobs();
      }
    }, 3000); // Refresh every 3 seconds

    return () => clearInterval(interval);
  }, [batchId, autoRefresh]);

  const loadBatchStatus = async () => {
    try {
      const response = await jobService.getBatchStatus(batchId);
      setBatchStatus(response.data);
      
      // Stop auto-refresh if batch is complete
      if (response.data.progress.remaining === 0) {
        setAutoRefresh(false);
      }
      
      setError(null);
    } catch (error: any) {
      setError(error.message || 'Failed to load batch status');
    } finally {
      setIsLoading(false);
    }
  };

  const loadRecentJobs = async () => {
    try {
      const response = await jobService.getJobs({
        batch_id: batchId,
        limit: 10
      });
      setRecentJobs(response.data.jobs);
    } catch (error: any) {
      console.error('Failed to load recent jobs:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-muted-foreground';
      case 'processing': return 'text-primary';
      case 'processed_pending_approval': return 'text-warning';
      case 'approved': return 'text-success';
      case 'complete': return 'text-success';
      case 'failed': return 'text-destructive';
      case 'cancelled': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'processing': return <Loader2 className="w-4 h-4 animate-spin" />;
      case 'processed_pending_approval': return <AlertCircle className="w-4 h-4" />;
      case 'approved': return <UserCheck className="w-4 h-4" />;
      case 'uploading_to_magento': return <Upload className="w-4 h-4 animate-pulse" />;
      case 'complete': return <CheckCircle2 className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'cancelled': return <X className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span>Loading batch status...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="w-4 h-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!batchStatus) {
    return null;
  }

  const isActive = batchStatus.progress.remaining > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="w-6 h-6" />
            Job Processing Monitor
          </h2>
          <p className="text-muted-foreground">
            Batch ID: {batchId.slice(0, 8)}...
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={isRefreshing}
            onClick={async () => {
              setIsRefreshing(true);
              try {
                await Promise.all([
                  loadBatchStatus(),
                  loadRecentJobs()
                ]);
              } catch (error) {
                console.error('Refresh failed:', error);
              } finally {
                setIsRefreshing(false);
              }
            }}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      {/* Progress Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Progress Overview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-muted/30 rounded-lg">
              <div className="text-2xl font-bold">{batchStatus.total_jobs}</div>
              <div className="text-sm text-muted-foreground">Total Jobs</div>
            </div>
            <div className="text-center p-3 bg-yellow-100 dark:bg-yellow-950/30 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {batchStatus.status_breakdown.processed_pending_approval}
              </div>
              <div className="text-sm text-muted-foreground">Pending Approval</div>
            </div>
            <div className="text-center p-3 bg-success/10 rounded-lg">
              <div className="text-2xl font-bold text-success">
                {batchStatus.status_breakdown.complete}
              </div>
              <div className="text-sm text-muted-foreground">Complete</div>
            </div>
            <div className="text-center p-3 bg-destructive/10 rounded-lg">
              <div className="text-2xl font-bold text-destructive">
                {batchStatus.status_breakdown.failed}
              </div>
              <div className="text-sm text-muted-foreground">Failed</div>
            </div>
          </div>

          {/* Approval Action */}
          {batchStatus.status_breakdown.processed_pending_approval > 0 && onViewApproval && (
            <div className="flex items-center justify-between p-4 bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                <div>
                  <div className="font-medium text-yellow-900 dark:text-yellow-200">Images Ready for Approval</div>
                  <div className="text-sm text-yellow-700 dark:text-yellow-300">
                    {batchStatus.status_breakdown.processed_pending_approval} image{batchStatus.status_breakdown.processed_pending_approval !== 1 ? 's' : ''} processed and ready for review
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={onViewApproval} className="bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-700 dark:hover:bg-yellow-800">
                  <UserCheck className="w-4 h-4 mr-2" />
                  Review & Approve
                </Button>
                {onBulkReject && (
                  <Button 
                    onClick={async () => {
                      // Get all pending approval jobs for this batch
                      try {
                        const response = await jobService.getJobs({
                          batch_id: batchId,
                          status: 'processed_pending_approval',
                          limit: 1000 // Get all pending jobs
                        });
                        if (response.data.jobs.length > 0) {
                          onBulkReject(response.data.jobs.map(job => job.id), 'Bulk rejection from job monitor');
                        }
                      } catch (error) {
                        console.error('Failed to get pending jobs for bulk reject:', error);
                      }
                    }}
                    variant="destructive"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject All
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Auto-refresh indicator */}
          {isActive && (
            <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                <span className="text-sm">Live monitoring active</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAutoRefresh(!autoRefresh)}
              >
                {autoRefresh ? 'Pause' : 'Resume'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Status Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(batchStatus.status_breakdown).map(([status, count]) => {
              if (count === 0) return null;
              
              return (
                <div key={status} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className={getStatusColor(status)}>
                      {getStatusIcon(status)}
                    </span>
                    <span className="font-medium capitalize">
                      {status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <Badge variant="secondary">{count}</Badge>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Recent Jobs */}
      {recentJobs.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Recent Jobs
              </CardTitle>
              {onViewJobs && (
                <Button variant="outline" size="sm" onClick={onViewJobs}>
                  View All Jobs
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentJobs.slice(0, 5).map((job) => {
                const statusDisplay = jobService.getStatusDisplay(job.status);
                
                return (
                  <div key={job.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <img
                        src={job.original_image_url || job.processed_image_url}
                        alt="Product"
                        className="w-10 h-10 rounded object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/placeholder-image.png';
                        }}
                      />
                      <div>
                        <div className="font-medium">{job.products.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {job.transformations.length} transformation{job.transformations.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant="secondary" 
                        className={`${statusDisplay.color} border-current`}
                      >
                        <span className="mr-1">{getStatusIcon(job.status)}</span>
                        {statusDisplay.label}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Completion Message */}
      {!isActive && batchStatus.progress.percentage === 100 && (
        <Alert>
          <CheckCircle2 className="w-4 h-4" />
          <AlertDescription>
            All jobs in this batch have been processed! 
            {batchStatus.status_breakdown.complete > 0 && 
              ` ${batchStatus.status_breakdown.complete} jobs completed successfully.`
            }
            {batchStatus.status_breakdown.failed > 0 && 
              ` ${batchStatus.status_breakdown.failed} jobs failed.`
            }
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}